import type { CalendarSpan } from './Calendar.types'
import moment from 'moment'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import type { TripItineraryItem, TripStop } from 'tripPlanner/api/types'
import { TRIP_LIST_CATEGORY_COLOURS } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ListView/tripListCategoryColours'
import { sortTripStopsByItineraryOrder } from 'tripPlanner/utils/itineraryStopOrder'
import { resolveStopDuration } from 'tripPlanner/utils/stopDuration'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'

export interface StopCalendarColours {
  foreground: string
  background: string
}

export const STOP_CALENDAR_COLOURS: ReadonlyArray<StopCalendarColours> = [
  { foreground: '#E8A44E', background: '#E8A44E26' },
  { foreground: '#599BF2', background: '#599BF226' },
  { foreground: '#80755A', background: '#80755A26' },
  { foreground: '#0F4C4C', background: '#0F4C4C26' },
  { foreground: '#C94922', background: '#C9492226' },
  { foreground: '#1DAF43', background: '#1DAF4326' },
]

export function getStopCalendarColours(index: number): StopCalendarColours {
  return STOP_CALENDAR_COLOURS[index % STOP_CALENDAR_COLOURS.length]
}

/*
 * Read from the list view's palette rather than restating it. These were three
 * verbatim copies of its hotel / transport / entertainment entries, so a change
 * there silently stopped matching here.
 */
function getCalendarItemColours(itemType: TripItineraryItem['type']): StopCalendarColours {
  switch (itemType) {
    case 'ACCOMMODATION': return TRIP_LIST_CATEGORY_COLOURS.hotel
    case 'FLIGHT': return TRIP_LIST_CATEGORY_COLOURS.transport
    default: return TRIP_LIST_CATEGORY_COLOURS['entertainment-and-leisure']
  }
}

export interface StopSpanData {
  stopId: number
  placeName: string
  colours: StopCalendarColours
  hasSharedStartBoundary: boolean
  hasSharedEndBoundary: boolean
  hasAdjacentStartBoundary: boolean
  hasAdjacentEndBoundary: boolean
}

export interface BookedAccommodationSpanData {
  kind: 'booked-accommodation'
  itemId: number
  label: string
  colours: StopCalendarColours
}

export type StopCalendarSpanData = StopSpanData | BookedAccommodationSpanData

export interface StopCalendarChip {
  id: string
  itemId: number
  label: string
  itemType: TripItineraryItem['type']
  colours: StopCalendarColours
}

export interface StopCalendarDayItem extends StopCalendarChip {
  startTime?: string
  stopName: string
  imageId?: string
}

function getCalendarItemImageId(item: TripItineraryItem): string | undefined {
  const savedItemData = item.savedItemData
  if (
    savedItemData
    && 'imageIds' in savedItemData
    && Array.isArray(savedItemData.imageIds)
    && typeof savedItemData.imageIds[0] === 'string'
  ) {
    return savedItemData.imageIds[0]
  }

  return item.itemPlaceDetail?.imageIds?.[0]
}

function isAdjacentDate(from: string | undefined, to: string | undefined): boolean {
  return Boolean(from && to && moment(from).add(1, 'day').format('YYYY-MM-DD') === to)
}

/**
 * The other stops already placed on the calendar, so someone choosing dates
 * for Sydney can see that Melbourne already occupies the 10th to the 13th.
 * The stop being edited is included but flagged, so it can be drawn
 * differently rather than filtered out and losing its place in the lanes.
 */
/** End day for a stop that carries a start and a night count but no end date. */
function deriveEndFromNights(stop: TripStop): string | undefined {
  const from = charterCalendarDayKey(stop.startDate)
  if (!from || stop.endDate) return undefined

  const duration = resolveStopDuration(stop)
  if (!duration) return undefined

  return moment(from, 'YYYY-MM-DD').add(duration.nightCount, 'days').format('YYYY-MM-DD')
}

export function buildStopSpans(
  stops: ReadonlyArray<TripStop> | undefined,
  _currentStopId?: number,
): Array<CalendarSpan<StopSpanData>> {
  if (!stops?.length) return []

  const orderedStops = sortTripStopsByItineraryOrder([...stops])
    .filter(stop => stop.stopType !== 'ORIGIN')

  /*
   * Colour follows a stop's place in the whole itinerary rather than among
   * the dated ones alone. The summary rail lists every destination and
   * colours them by that order, so indexing the bars over a shorter, dated
   * only list hands the same stop two different colours the moment an
   * undated one sits before it. A stop with no dates simply reserves a
   * colour no bar draws.
   */
  const colourIndexByStopId = new Map(orderedStops.map((stop, index) => [stop.id, index]))

  /*
   * Normalise once, here. The API types these as plain strings, and a stop
   * carrying '2026-10-09T00:00:00.000Z' next to one carrying '2026-10-09' is
   * the same calendar day -- but every comparison below is `===`, and a raw
   * mismatch reads as "no changeover", stacking two bars that should split a
   * day. Parsing with the input's own offset keeps the day the source meant.
   */
  const datedStops = orderedStops
    .map(stop => ({
      stop,
      from: charterCalendarDayKey(stop.startDate),
      /*
       * The contract types startDate and endDate independently optional, and a
       * stop with a start and no end used to be filtered out entirely -- no
       * bar, and its month never mounted. `resolveStopDuration` is the
       * codebase's existing answer to that shape: it falls back to
       * `numberOfNights`, which TripStop carries.
       */
      to: charterCalendarDayKey(stop.endDate) ?? deriveEndFromNights(stop),
    }))
    .filter((entry): entry is typeof entry & { from: string, to: string } => (
      Boolean(entry.from && entry.to && entry.from <= entry.to)
    ))

  /*
   * Neighbours come from date order, not sequence order. `sortTripStopsByItineraryOrder`
   * sorts on `stopSeq` alone, while `assignSpanLanes` packs by date -- so an
   * itinerary whose stopSeq has drifted from its dates (a streamed `stop_added`
   * splices by seq and never re-sequences) would compute a changeover against
   * one neighbour and pack lanes against another. Same input, different render.
   */
  const byDate = [...datedStops].sort((a, b) => (
    a.from < b.from ? -1 : a.from > b.from ? 1 : (a.stop.stopSeq - b.stop.stopSeq)
  ))
  const dateOrderById = new Map(byDate.map((entry, index) => [entry.stop.id, index]))

  return datedStops.map(({ stop, from, to }, index): CalendarSpan<StopSpanData> => {
    const dateIndex = dateOrderById.get(stop.id) ?? index
    const previousStop = byDate[dateIndex - 1]
    const nextStop = byDate[dateIndex + 1]
    const hasSharedStartBoundary = previousStop?.to === from
    const hasSharedEndBoundary = nextStop?.from === to
    const hasAdjacentStartBoundary = isAdjacentDate(previousStop?.to, from)
    const hasAdjacentEndBoundary = isAdjacentDate(to, nextStop?.from)

    return {
      id: `stop-${stop.id}`,
      from,
      to,
      shareLaneAtBoundary: hasSharedStartBoundary || hasSharedEndBoundary,
      data: {
        stopId: stop.id,
        placeName: stop.placeName || stop.placeCountry || '',
        colours: getStopCalendarColours(colourIndexByStopId.get(stop.id) ?? index),
        hasSharedStartBoundary,
        hasSharedEndBoundary,
        hasAdjacentStartBoundary,
        hasAdjacentEndBoundary,
      },
    }
  })
}

function toChip(item: TripItineraryItem): StopCalendarChip {
  return {
    id: `item-${item.id}`,
    itemId: item.id,
    label: item.name,
    itemType: item.type,
    colours: getCalendarItemColours(item.type),
  }
}

function getBookedAccommodationDateRange(
  item: TripItineraryItem,
  fallbackCheckIn: string,
): { from: string, to: string } | undefined {
  if (item.type !== 'ACCOMMODATION' || !('isBooked' in item) || !item.isBooked) return undefined

  const savedItemData = isHotelSavedData(item.savedItemData) ? item.savedItemData : undefined

  const itemStartDate = 'startDate' in item && typeof item.startDate === 'string' ? item.startDate : undefined
  const itemEndDate = 'endDate' in item && typeof item.endDate === 'string' ? item.endDate : undefined
  const bookedRoom = savedItemData?.bookedRooms?.find(
    (room): room is typeof room & { checkIn: string, checkOut: string } => (
      'checkIn' in room
      && 'checkOut' in room
      && typeof room.checkIn === 'string'
      && typeof room.checkOut === 'string'
    ),
  )
  const checkIn = savedItemData?.checkIn ?? bookedRoom?.checkIn ?? itemStartDate ?? fallbackCheckIn
  const derivedCheckOut = typeof item.totalDays === 'number' && item.totalDays > 1
    ? moment(checkIn).add(item.totalDays - 1, 'days').format('YYYY-MM-DD')
    : undefined
  const checkOut = savedItemData?.checkOut ?? bookedRoom?.checkOut ?? itemEndDate ?? derivedCheckOut
  if (!checkIn || !checkOut || checkOut < checkIn) return undefined

  return { from: checkIn, to: checkOut }
}

export function buildBookedAccommodationSpans(
  stops: ReadonlyArray<TripStop> | undefined,
): Array<CalendarSpan<BookedAccommodationSpanData>> {
  if (!stops?.length) return []

  const spansByItemId = new Map<string, CalendarSpan<BookedAccommodationSpanData>>()
  for (const stop of stops) {
    if (stop.stopType === 'ORIGIN') continue

    for (const day of stop.days) {
      const dayDate = day.date
      if (!dayDate || !day.items?.length) continue

      for (const item of day.items) {
        const dateRange = getBookedAccommodationDateRange(item, dayDate)
        if (!dateRange) continue

        const id = `booked-accommodation-${item.id}`
        if (spansByItemId.has(id)) continue

        spansByItemId.set(id, {
          id,
          from: dateRange.from,
          to: dateRange.to,
          laneOrder: 1,
          data: {
            kind: 'booked-accommodation',
            itemId: item.id,
            label: item.name,
            colours: getCalendarItemColours('ACCOMMODATION'),
          },
        })
      }
    }
  }

  return [...spansByItemId.values()]
}

/**
 * What is already planned on each date, keyed by ISO date, so a day tile can
 * show the experiences and bookings that live on it.
 */
export function buildStopCalendarChips(
  stops: ReadonlyArray<TripStop> | undefined,
): Map<string, Array<StopCalendarChip>> {
  const chipsByDate = new Map<string, Array<StopCalendarChip>>()
  if (!stops?.length) return chipsByDate

  for (const stop of stops) {
    if (stop.stopType === 'ORIGIN') continue

    for (const day of stop.days) {
      const dayDate = day.date
      if (!dayDate || !day.items?.length) continue

      // A multi-day stay repeats across its nights; only the arrival earns a chip.
      const chips = day.items
        .filter(item => 'isBooked' in item && item.isBooked)
        .filter(item => !getBookedAccommodationDateRange(item, dayDate))
        .filter(item => (item.totalDays ?? 1) <= 1 || item.dayOfStay === 1 || item.isCheckIn === true)
        .map(toChip)

      if (!chips.length) continue

      const existing = chipsByDate.get(dayDate)
      if (existing) {
        existing.push(...chips)
      } else {
        chipsByDate.set(dayDate, chips)
      }
    }
  }

  return chipsByDate
}

/**
 * The same items again, but keeping the time and which stop they belong to,
 * for the condensed day view beside the calendar.
 */
export function buildStopCalendarDayItems(
  stops: ReadonlyArray<TripStop> | undefined,
): Map<string, Array<StopCalendarDayItem>> {
  const itemsByDate = new Map<string, Array<StopCalendarDayItem>>()
  if (!stops?.length) return itemsByDate

  for (const stop of stops) {
    const stopName = stop.placeName || stop.placeCountry || ''
    for (const day of stop.days) {
      if (!day.date || !day.items?.length) continue

      const dayItems = day.items
        .filter(item => (item.totalDays ?? 1) <= 1 || item.dayOfStay === 1 || item.isCheckIn === true)
        .map((item): StopCalendarDayItem => ({
          ...toChip(item),
          startTime: 'startTime' in item && typeof item.startTime === 'string' ? item.startTime : undefined,
          stopName,
          imageId: getCalendarItemImageId(item),
        }))

      if (!dayItems.length) continue

      const existing = itemsByDate.get(day.date)
      if (existing) {
        existing.push(...dayItems)
      } else {
        itemsByDate.set(day.date, dayItems)
      }
    }
  }

  return itemsByDate
}
